// Nutrición y dietas terapéuticas. Catering → Operación → Nutrición y dietas.
// (1) Composición nutricional de la materia prima (por unidad base del producto),
// (2) aporte calculado por porción de cada receta/plato con validación contra una
// dieta (semáforo por nutriente), (3) catálogo de tipos de dieta con objetivos.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Apple, FlaskConical, ClipboardCheck, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, num } from '@/features/inventario/shared';
import { catRpc, invalidateCatering } from './shared';

interface MpNutri {
  producto_id: string; codigo: string; nombre: string; unidad: string;
  energia_kcal: number | null; proteina_g: number | null; grasa_g: number | null;
  carbohidrato_g: number | null; fibra_g: number | null; sodio_mg: number | null;
  fuente: string | null; tiene_datos: boolean;
}
interface Aporte { energia_kcal: number; proteina_g: number; grasa_g: number; carbohidrato_g: number; fibra_g: number; sodio_mg: number }
interface RecetaNutri {
  id: string; nombre: string; tipo: string; categoria: string | null;
  nutricion: { cobertura_pct: number; mp_total: number; mp_con_datos: number; por_porcion: Aporte; sin_datos: string[] };
}
interface Dieta {
  id: string; codigo: string | null; nombre: string; descripcion: string | null; es_terapeutica: boolean;
  energia_min: number | null; energia_max: number | null; proteina_min: number | null; proteina_max: number | null;
  grasa_max: number | null; cho_max: number | null; sodio_max: number | null; notas: string | null; es_activo: boolean;
}

const NUTRIENTES: { k: keyof Aporte; label: string; u: string }[] = [
  { k: 'energia_kcal', label: 'Energía', u: 'kcal' }, { k: 'proteina_g', label: 'Proteína', u: 'g' },
  { k: 'grasa_g', label: 'Grasa', u: 'g' }, { k: 'carbohidrato_g', label: 'Carbohidrato', u: 'g' },
  { k: 'fibra_g', label: 'Fibra', u: 'g' }, { k: 'sodio_mg', label: 'Sodio', u: 'mg' },
];

// Semáforo cliente: compara un aporte contra los objetivos de la dieta
function evaluar(a: Aporte, d: Dieta) {
  const chk = (val: number, min: number | null, max: number | null): 'ok' | 'alto' | 'bajo' | 'na' => {
    if (min == null && max == null) return 'na';
    if (max != null && val > max) return 'alto';
    if (min != null && val < min) return 'bajo';
    return 'ok';
  };
  return {
    energia: chk(a.energia_kcal, d.energia_min, d.energia_max),
    proteina: chk(a.proteina_g, d.proteina_min, d.proteina_max),
    grasa: chk(a.grasa_g, null, d.grasa_max),
    cho: chk(a.carbohidrato_g, null, d.cho_max),
    sodio: chk(a.sodio_mg, null, d.sodio_max),
  };
}
const CLS: Record<string, string> = {
  ok: 'text-green-700 dark:text-green-400', alto: 'text-red-600 font-medium',
  bajo: 'text-amber-600 font-medium', na: 'text-muted-foreground',
};
const MARCA: Record<string, string> = { ok: '✓', alto: '▲ alto', bajo: '▼ bajo', na: '—' };

export function NutricionCatering() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'aporte' | 'composicion' | 'dietas'>('aporte');
  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap gap-1">
        {([['aporte', 'Aporte por receta', FlaskConical], ['composicion', 'Composición de MP', Apple], ['dietas', 'Tipos de dieta', ClipboardCheck]] as const).map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm ${tab === k ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>
      {tab === 'aporte' && <TabAporte qc={qc} />}
      {tab === 'composicion' && <TabComposicion qc={qc} />}
      {tab === 'dietas' && <TabDietas qc={qc} />}
    </div>
  );
}

function TabAporte({ qc }: { qc: ReturnType<typeof useQueryClient> }) {
  const [tipo, setTipo] = useState('');
  const [dietaId, setDietaId] = useState('');
  const { data: recetas = [] } = useQuery<RecetaNutri[]>({ queryKey: ['cat-recetas-nutri', tipo], queryFn: () => catRpc<RecetaNutri[]>('cat_recetas_nutricion', { _tipo: tipo }) });
  const { data: dietas = [] } = useQuery<Dieta[]>({ queryKey: ['cat-dietas'], queryFn: () => catRpc<Dieta[]>('cat_dieta_listar') });
  const dieta = dietas.find((d) => d.id === dietaId) ?? null;
  void qc;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Aporte nutricional por porción</CardTitle>
        <div className="flex flex-wrap items-end gap-2 pt-1">
          <select className={`${fieldCls} w-44`} value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="">Todas</option><option value="plato">Platos (lo que recibe el comensal)</option><option value="subproducto">Recetas base</option>
          </select>
          <div>
            <label className="block text-xs text-muted-foreground">Validar contra dieta</label>
            <select className={`${fieldCls} w-56`} value={dietaId} onChange={(e) => setDietaId(e.target.value)}>
              <option value="">(sin validar)</option>
              {dietas.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-1 pr-2">Receta</th><th className="py-1 pr-2 text-right">kcal</th>
            <th className="py-1 pr-2 text-right">Prot</th><th className="py-1 pr-2 text-right">Grasa</th>
            <th className="py-1 pr-2 text-right">CHO</th><th className="py-1 pr-2 text-right">Na (mg)</th>
            <th className="py-1 pr-2 text-right">Cobertura</th>{dieta && <th className="py-1 pr-2">vs {dieta.nombre}</th>}
          </tr></thead>
          <tbody>
            {recetas.map((r) => {
              const a = r.nutricion.por_porcion; const ev = dieta ? evaluar(a, dieta) : null;
              return (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="py-1.5 pr-2">{r.nombre}<span className="ml-1 rounded-full bg-muted px-1.5 text-[10px]">{r.tipo === 'plato' ? 'plato' : 'base'}</span></td>
                  <td className="py-1.5 pr-2 text-right font-mono">{num(a.energia_kcal, 0)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{num(a.proteina_g, 1)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{num(a.grasa_g, 1)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{num(a.carbohidrato_g, 1)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{num(a.sodio_mg, 0)}</td>
                  <td className={`py-1.5 pr-2 text-right font-mono ${r.nutricion.cobertura_pct < 80 ? 'text-amber-600' : 'text-muted-foreground'}`} title={r.nutricion.sin_datos.length ? 'Sin datos: ' + r.nutricion.sin_datos.join(', ') : ''}>{r.nutricion.cobertura_pct}%</td>
                  {ev && (
                    <td className="py-1.5 pr-2 text-xs">
                      <span className={CLS[ev.energia]}>kcal {MARCA[ev.energia]}</span>{' · '}
                      <span className={CLS[ev.proteina]}>prot {MARCA[ev.proteina]}</span>{' · '}
                      <span className={CLS[ev.sodio]}>Na {MARCA[ev.sodio]}</span>
                    </td>
                  )}
                </tr>
              );
            })}
            {!recetas.length && <tr><td colSpan={dieta ? 8 : 7} className="py-2 text-sm text-muted-foreground">Sin recetas.</td></tr>}
          </tbody>
        </table>
        <p className="pt-2 text-xs text-muted-foreground">
          El aporte se calcula explotando la receta a materia prima. La <b>cobertura</b> indica qué % de la MP tiene datos nutricionales: por debajo de 100% el aporte es parcial. Completa los faltantes en "Composición de MP".
        </p>
      </CardContent>
    </Card>
  );
}

function TabComposicion({ qc }: { qc: ReturnType<typeof useQueryClient> }) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<MpNutri | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const { data: mps = [] } = useQuery<MpNutri[]>({ queryKey: ['cat-nutri-mp'], queryFn: () => catRpc<MpNutri[]>('cat_nutricion_productos') });
  const cobertura = useMemo(() => mps.length ? Math.round(100 * mps.filter((m) => m.tiene_datos).length / mps.length) : 0, [mps]);
  const filtradas = useMemo(() => mps.filter((m) => m.nombre.toLowerCase().includes(q.toLowerCase())), [mps, q]);

  const abrir = (m: MpNutri) => {
    setSel(m);
    setForm({ energia_kcal: String(m.energia_kcal ?? ''), proteina_g: String(m.proteina_g ?? ''), grasa_g: String(m.grasa_g ?? ''),
      carbohidrato_g: String(m.carbohidrato_g ?? ''), fibra_g: String(m.fibra_g ?? ''), sodio_mg: String(m.sodio_mg ?? ''), fuente: m.fuente ?? '' });
  };
  const guardar = useMutation({
    mutationFn: () => catRpc('cat_nutricion_producto_set', { _producto_id: sel!.producto_id, _data: form }),
    onSuccess: () => { toast.success('Composición guardada'); setSel(null); void qc.invalidateQueries({ queryKey: ['cat-nutri-mp'] }); invalidateCatering(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
      <Card className="lg:col-span-3">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Composición de materias primas</CardTitle>
          <p className="pt-1 text-xs text-muted-foreground">Cobertura: <b>{cobertura}%</b> ({mps.filter((m) => m.tiene_datos).length}/{mps.length} MP con datos). Los valores son <b>por 1 unidad base</b> del producto (por kg, l o unidad).</p>
          <div className="relative pt-1"><Search className="absolute left-2 top-3.5 h-3.5 w-3.5 text-muted-foreground" /><input className={`${fieldCls} w-full pl-7`} placeholder="Buscar materia prima…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        </CardHeader>
        <CardContent className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1 pr-2">Materia prima</th><th className="py-1 pr-2">Unidad</th><th className="py-1 pr-2 text-right">kcal</th><th className="py-1 pr-2 text-right">Prot</th><th className="py-1 pr-2 text-right">Na</th><th /></tr></thead>
            <tbody>
              {filtradas.map((m) => (
                <tr key={m.producto_id} className={`border-b last:border-0 ${!m.tiene_datos ? 'bg-amber-50/40 dark:bg-amber-900/10' : ''} ${sel?.producto_id === m.producto_id ? 'bg-accent' : ''}`}>
                  <td className="py-1.5 pr-2">{m.nombre}</td>
                  <td className="py-1.5 pr-2 text-xs text-muted-foreground">{m.unidad}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{m.tiene_datos ? num(m.energia_kcal ?? 0, 0) : '—'}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{m.tiene_datos ? num(m.proteina_g ?? 0, 1) : '—'}</td>
                  <td className="py-1.5 pr-2 text-right font-mono">{m.tiene_datos ? num(m.sodio_mg ?? 0, 0) : '—'}</td>
                  <td className="py-1.5 text-right"><button className="text-xs text-primary underline" onClick={() => abrir(m)}>{m.tiene_datos ? 'editar' : 'agregar'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      <Card className="lg:col-span-2">
        <CardHeader className="pb-2"><CardTitle className="text-base">{sel ? sel.nombre : 'Editar composición'}</CardTitle>{sel && <p className="text-xs text-muted-foreground">Valores por 1 {sel.unidad}</p>}</CardHeader>
        <CardContent className="space-y-2">
          {!sel ? <p className="text-sm text-muted-foreground">Selecciona una materia prima.</p> : (
            <>
              {NUTRIENTES.map((n) => (
                <div key={n.k} className="flex items-center gap-2">
                  <label className="w-40 text-sm">{n.label} <span className="text-xs text-muted-foreground">({n.u})</span></label>
                  <input className={`${fieldCls} w-32`} type="number" step="0.01" value={form[n.k] ?? ''} onChange={(e) => setForm((s) => ({ ...s, [n.k]: e.target.value }))} />
                </div>
              ))}
              <div className="flex items-center gap-2"><label className="w-40 text-sm">Fuente</label><input className={`${fieldCls} flex-1`} placeholder="TCA, etiqueta…" value={form.fuente ?? ''} onChange={(e) => setForm((s) => ({ ...s, fuente: e.target.value }))} /></div>
              <div className="flex gap-2 pt-1"><Button size="sm" disabled={guardar.isPending} onClick={() => guardar.mutate()}>Guardar</Button><Button size="sm" variant="ghost" onClick={() => setSel(null)}>Cancelar</Button></div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TabDietas({ qc }: { qc: ReturnType<typeof useQueryClient> }) {
  const { data: dietas = [] } = useQuery<Dieta[]>({ queryKey: ['cat-dietas'], queryFn: () => catRpc<Dieta[]>('cat_dieta_listar') });
  const [edit, setEdit] = useState<Partial<Dieta> | null>(null);
  const guardar = useMutation({
    mutationFn: (d: Partial<Dieta>) => catRpc('cat_dieta_guardar', {
      _data: {
        nombre: d.nombre, descripcion: d.descripcion ?? '', es_terapeutica: d.es_terapeutica ?? false,
        energia_min: d.energia_min ?? '', energia_max: d.energia_max ?? '', proteina_min: d.proteina_min ?? '',
        proteina_max: d.proteina_max ?? '', grasa_max: d.grasa_max ?? '', cho_max: d.cho_max ?? '',
        sodio_max: d.sodio_max ?? '', notas: d.notas ?? '', es_activo: d.es_activo ?? true,
      }, _id: d.id ?? null,
    }),
    onSuccess: () => { toast.success('Dieta guardada'); setEdit(null); void qc.invalidateQueries({ queryKey: ['cat-dietas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const numField = (k: keyof Dieta, label: string) => (
    <div><label className="block text-[11px] text-muted-foreground">{label}</label>
      <input className={`${fieldCls} w-24`} type="number" value={(edit?.[k] as number | undefined) ?? ''} onChange={(e) => setEdit((s) => ({ ...s!, [k]: e.target.value === '' ? null : Number(e.target.value) }))} /></div>
  );

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base">Tipos de dieta y objetivos</CardTitle>
        <Button size="sm" variant="outline" onClick={() => setEdit({ es_activo: true, es_terapeutica: true })}><Plus className="mr-1 h-3.5 w-3.5" /> Nueva</Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {edit && (
          <div className="space-y-2 rounded-md border p-3">
            <div className="flex flex-wrap items-end gap-2">
              <div><label className="block text-[11px] text-muted-foreground">Nombre *</label><input className={`${fieldCls} w-56`} value={edit.nombre ?? ''} onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} /></div>
              <label className="flex items-center gap-1 pb-2 text-sm"><input type="checkbox" checked={edit.es_terapeutica ?? false} onChange={(e) => setEdit({ ...edit, es_terapeutica: e.target.checked })} /> terapéutica</label>
            </div>
            <input className={`${fieldCls} w-full`} placeholder="Descripción" value={edit.descripcion ?? ''} onChange={(e) => setEdit({ ...edit, descripcion: e.target.value })} />
            <div className="flex flex-wrap gap-2">
              {numField('energia_min', 'kcal mín')}{numField('energia_max', 'kcal máx')}
              {numField('proteina_min', 'prot mín')}{numField('proteina_max', 'prot máx')}
              {numField('grasa_max', 'grasa máx')}{numField('cho_max', 'CHO máx')}{numField('sodio_max', 'Na máx (mg)')}
            </div>
            <input className={`${fieldCls} w-full`} placeholder="Notas" value={edit.notas ?? ''} onChange={(e) => setEdit({ ...edit, notas: e.target.value })} />
            <div className="flex gap-2"><Button size="sm" disabled={!edit.nombre || guardar.isPending} onClick={() => guardar.mutate(edit)}>Guardar</Button><Button size="sm" variant="ghost" onClick={() => setEdit(null)}>Cancelar</Button></div>
          </div>
        )}
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-1 pr-2">Dieta</th><th className="py-1 pr-2">kcal</th><th className="py-1 pr-2">Proteína</th><th className="py-1 pr-2">Na máx</th><th /></tr></thead>
          <tbody>
            {dietas.map((d) => (
              <tr key={d.id} className={`border-b last:border-0 ${!d.es_activo ? 'opacity-50' : ''}`}>
                <td className="py-1.5 pr-2">{d.nombre}{d.es_terapeutica && <span className="ml-1 rounded-full bg-amber-100 px-1.5 text-[10px] text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">terapéutica</span>}<span className="block text-xs text-muted-foreground">{d.descripcion}</span></td>
                <td className="py-1.5 pr-2 font-mono text-xs">{d.energia_min ?? '·'}–{d.energia_max ?? '·'}</td>
                <td className="py-1.5 pr-2 font-mono text-xs">{d.proteina_min ?? '·'}–{d.proteina_max ?? '·'} g</td>
                <td className="py-1.5 pr-2 font-mono text-xs">{d.sodio_max != null ? `${d.sodio_max} mg` : '—'}</td>
                <td className="py-1.5 text-right"><button className="text-xs text-primary underline" onClick={() => setEdit(d)}>editar</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
